"use client";
import React from "react";
import Image from "next/image";
import { Section, Typography } from "@/components/ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { env } from "@/lib/environment";

import "swiper/css";

interface SpeakYourCultureSectionProps {
  imageSrc: string;
  imageAlt: string;
  videoSrc?: string;
  className?: string;
}

export default function SpeakYourCultureSection({
  imageSrc,
  imageAlt,
  className = "",
}: SpeakYourCultureSectionProps) {
  const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/industries/corporate`;

  const sliderImages = [
    {
      src: `${imgBaseUrl}/screen-speak-culture-2.MuadcMHyiiXu.webp`,
      alt: "Corporate digital signage screen 2",
    },
    {
      src: `${imgBaseUrl}/screen-speak-culture-1.aqg4NpuW68d0.jpg`,
      alt: "Corporate digital signage screen 1",
    },
    {
      src: `${imgBaseUrl}/screen-speak-culture-3.EKDFQxGZDP9Y.webp`,
      alt: "Corporate digital signage screen 3",
    },
  ];

  return (
    <Section variant="default" background="white" className={className}>
      <div className="max-w-7xl container mx-auto">
        {/* Title and Description */}
        <div className="text-center mb-12">
          <Typography variant="h2" as="h2" className="mb-6">
            Screens that speak your culture.
          </Typography>
          <Typography variant="body" as="p" className="text-text-primary max-w-4xl mx-auto">
            From lobbies to meeting rooms, Pickcel keeps your workplace informed and energised —
            through visual, timely communication.
          </Typography>
        </div>

        {/* Office Scene with Digital Display and Video Overlay */}
        <div className="relative w-full overflow-hidden rounded-lg">
          <Image
            src={imageSrc}
            alt={imageAlt}
            title={imageAlt}
            width={1200}
            height={700}
            className="w-full h-auto rounded-lg"
          />

          {/* Image Slider Overlay on Screen */}
          <div className="hidden lg:block absolute bottom-[-8px] left-1/2 -translate-x-1/2 aspect-video max-w-[752px] overflow-hidden shadow-2xl border-3 border-gray-800/30 bg-black">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              loop={true}
              className="w-full h-full [&_.swiper-pagination]:hidden"
            >
              {sliderImages.map((image, index) => (
                <SwiperSlide key={index}>
                  <div className="w-full h-full flex items-center justify-center">
                    <Image
                      src={image.src}
                      alt={image.alt}
                      title={image.alt}
                      width={752}
                      height={423}
                      className="w-full h-full object-cover"
                      priority={index === 0}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </Section>
  );
}
